'use client'

import { REAL_ESTATE_PARTNERS, RealEstatePartner } from '@/app/[lang]/partners/immo/real-estate-partners'
import { cn } from '@/lib/utils/utils'
import { X } from 'lucide-react'
import Image from 'next/image'
import { useCallback, useEffect, useState } from 'react'
import { getLangCode, I18nDictionary, LangCode, useI18n, useI18nValue } from '../i18n/lang-context'
import { Button } from '../ui/button'
import { Flex } from '../ui/flex'
import { ScrollDirection, useScroll } from '../utils/windowScroll'
import { useScreen } from '../utils/windowSize'

const SOURCE_COOKIE_NAME = 'partner'
const SOURCE_BANNER_CLOSED_COOKIE_NAME = 'partner_banner_closed'

// Translations
const I18N_BANNER: I18nDictionary = {
  message: {
    en: 'Welcome from {partner}',
    fr: 'Bienvenue de la part de {partner}'
  },
  defaultMessage: {
    en: 'Welcome! You arrived from our partner - Discover our services',
    fr: 'Bienvenue ! Vous venez de notre partenaire - Découvrez nos services'
  }
}

export function SourceBanner() {
  const screen = useScreen()
  const { scrollDir, scrollY } = useScroll(1)
  const [shown, setShown] = useState(false)
  const [initialRender, setInitialRender] = useState(true)
  const [source, setSource] = useState<string | null>(null)
  const [partner, setPartner] = useState<RealEstatePartner | null>(null)
  const [isClosed, setIsClosed] = useState(false)
  const i18n = useI18n(I18N_BANNER)
  const i18nValue = useI18nValue()
  const lang = getLangCode() as LangCode

  let onMouseLeaveTimeoutFunc: ReturnType<typeof setTimeout> | null = null

  const show = useCallback(() => shown || scrollDir === ScrollDirection.up || scrollY < 100, [shown, scrollDir, scrollY])

  const handleMouseLeave = () => {
    if (onMouseLeaveTimeoutFunc) clearTimeout(onMouseLeaveTimeoutFunc)
    onMouseLeaveTimeoutFunc = setTimeout(() => setShown(false), 0)
  }

  const handleMouseEnter = () => {
    if (!screen.md) return
    if (onMouseLeaveTimeoutFunc) {
      clearTimeout(onMouseLeaveTimeoutFunc)
      onMouseLeaveTimeoutFunc = null
    }
    setShown(true)
  }

  const handleClose = () => {
    // Set cookie to remember banner was closed
    document.cookie = `${SOURCE_BANNER_CLOSED_COOKIE_NAME}=true; path=/; max-age=${60 * 60 * 24 * 365}; secure; samesite=lax`
    setIsClosed(true)
  }

  useEffect(() => {
    // Check if banner was previously closed
    const cookies = document.cookie.split(';')
    const closedCookie = cookies.find(cookie => cookie.trim().startsWith(`${SOURCE_BANNER_CLOSED_COOKIE_NAME}=`))
    if (closedCookie) {
      const value = closedCookie.split('=')[1]
      if (value === 'true') {
        setIsClosed(true)
        return
      }
    }

    // Check for source cookie
    const sourceCookie = cookies.find(cookie => cookie.trim().startsWith(`${SOURCE_COOKIE_NAME}=`))
    if (sourceCookie) {
      const value = sourceCookie.split('=')[1]
      setSource(value)

      // Find partner by ID
      const foundPartner = REAL_ESTATE_PARTNERS.find(p => p.id === value)
      if (foundPartner) {
        setPartner(foundPartner)
      }
    }
  }, [])

  useEffect(() => {
    return () => {
      if (onMouseLeaveTimeoutFunc) clearTimeout(onMouseLeaveTimeoutFunc)
    }
  }, [])

  useEffect(() => {
    if (initialRender) {
      if (scrollY > 0) {
        setShown(true)
      }
      setInitialRender(false)
    }
  }, [scrollY, initialRender])

  // Don't render if no source or banner was closed
  if (!source || isClosed) return null

  const bannerTransform = {
    transform: `translateY(${show() ? 0 : screen.md ? 80 : 60}px)`,
    opacity: show() ? 1 : 0
  }

  // Prepare message
  let message = i18n.defaultMessage
  if (partner) {
    message = i18n.message.replace('{partner}', partner.name)
  }

  return (
    <div
      className='fixed bottom-0 left-0 z-40 w-full'
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      <div
        className={cn(
          'w-full bg-primary text-primary-foreground transition-all duration-300',
          show() ? 'shadow-lg' : ''
        )}
        style={bannerTransform}
      >
        <div className='m-auto flex w-full max-w-7xl items-center justify-between gap-4 p-4'>
          <Flex className='items-center gap-4'>
            {partner && (
              <a
                href={partner.website[lang]}
                target='_blank'
                rel='noopener noreferrer'
                className='relative flex h-12 items-center transition-opacity hover:opacity-80 md:h-16'
              >
                <Image
                  src={partner.logo}
                  alt={partner.name}
                  width={partner.logoW}
                  height={partner.logoH}
                  className='h-full w-auto object-contain'
                  style={{ maxHeight: '100%' }}
                />
              </a>
            )}
            <div>
              <p className='text-sm font-medium md:text-base'>{message}</p>
              {partner && partner.description && (
                <p className='text-xs opacity-90 md:text-sm'>
                  {i18nValue(partner.description)}
                </p>
              )}
            </div>
          </Flex>
          <Button
            variant='ghost'
            size='sm'
            onClick={handleClose}
            className='hover:bg-primary-foreground/20 text-primary-foreground'
            aria-label='Close banner'
          >
            <X className='h-4 w-4' />
          </Button>
        </div>
      </div>
    </div>
  )
}
